import { useCallback, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { usePortalSystems } from 'hooks/usePortalSystems';
import Workflow, { ActionStatus, WorkflowAction } from 'models/Workflow';
import { portalConnectionQueryKeys } from 'services/api/portalConnections';
import { indexPortalScripts, showPortalScript } from 'services/api/portalScripts';
import { showWorkflow, updateWorkflow, workflowQueryKeys, WorkflowValuesInput } from 'services/api/workflows';
import { PortalRetrievalStatus, runPortalRetrieval } from 'services/portalExtension';
import { useToastActions } from 'stores/toastStore';

import { scanItemFieldsFor } from '../constants';

import { ACTION_KINDS } from './constants';

/**
 * Executing a sweep in extension mode (M2).
 *
 * Starting a scan and RUNNING one are two different things. `POST /portal_scans` creates the run
 * and stops there — in headless mode a worker picks it up, but in extension mode the sweep happens
 * in this browser, driven by the extension, exactly as the retired `/portal-automation` page drove
 * it. Without this the Start Scan button produced a run that sat in `action_required` forever while
 * the banner claimed a scan was in progress.
 *
 * The shape is the discovery half of the old page, with the record-a-new-script branch left out:
 * `can_scan?` already refuses a portal with no discovery recording, so by the time a run exists
 * there is always a script to replay. Recording the first one is its own problem (W5-4 — a new
 * portal has no address to record from), and it is not this hook's.
 */

// A sweep's one output: the pointers it found. Each becomes its own retrieval run downstream.
const DISCOVERED_ITEMS = 'discovered_items';

export interface ScanRunState {
  workflowId: string;
  status: PortalRetrievalStatus | 'starting';
}

export function outstandingDiscovery(workflow: Workflow): WorkflowAction | undefined {
  return workflow.actions.find(
    (action) =>
      action.kind === ACTION_KINDS.discover &&
      (action.status === ActionStatus.ACTION_REQUIRED || action.status === ActionStatus.ERRORED)
  );
}

export default function useScanRunner() {
  const queryClient = useQueryClient();
  const { addToast } = useToastActions();
  const { systems: portalSystems } = usePortalSystems();
  const [active, setActive] = useState<ScanRunState | null>(null);
  // The extension runs one job at a time browser-wide, so a multi-portal start has to queue
  // rather than fan out — a second concurrent launch is answered with an error, not a second job.
  const busy = useRef(false);

  // Takes an id, not a row: a run this browser just created is not in the list yet, and a list
  // row is a projection that may carry less than the sweep needs. One fetch removes both worries.
  const runScan = useCallback(
    async (workflowId: string): Promise<boolean> => {
      if (busy.current) return false;

      busy.current = true;
      setActive({ workflowId, status: 'starting' });

      try {
        const workflow = await showWorkflow(workflowId);
        const action = outstandingDiscovery(workflow);
        if (!action) return false;

        const sourceSystem = String(workflow.valueFor('source_system')?.value ?? '');
        const portalUrl = String(workflow.valueFor('portal_url')?.value ?? '');

        // Purpose matters: the api defaults a script lookup to `retrieval`, so an unfiltered one
        // would replay a per-referral script against the portal's home page.
        const scripts = await indexPortalScripts(sourceSystem, undefined, 'discovery');
        const script = scripts?.[0];
        if (!script) throw new Error('This portal has no sweep recording to replay.');

        const detail = await showPortalScript(script.id);
        const values = await runPortalRetrieval(
          {
            script: { entryUrl: detail.entryUrl, body: detail.body, retrievalUrl: detail.retrievalUrl },
            // The run's own `portal_url` is the current truth; the recorded entry URL is where the
            // script happened to be made.
            entryUrl: portalUrl || detail.entryUrl,
            // A sweep takes no per-referral navigation params — there is no patient yet.
            params: {},
            context: { workflowId, actionId: action.id, sourceSystem, purpose: 'discovery' },
            expectedOutputs: action.produces.length > 0 ? action.produces : [DISCOVERED_ITEMS],
            itemFields: scanItemFieldsFor(portalSystems, sourceSystem),
          },
          { onStatus: (status) => setActive({ workflowId, status }) }
        );

        // An empty sweep is a real result — "nothing new in the portal" — so there is no
        // empty-capture guard. Completing with an empty list is what closes out a sweep that
        // found nothing.
        await updateWorkflow({
          workflowId,
          actionCompletions: [{ id: action.id, outputs: values as WorkflowValuesInput }],
        });

        queryClient.invalidateQueries({ queryKey: workflowQueryKeys.lists });
        queryClient.invalidateQueries({ queryKey: workflowQueryKeys.show(workflowId) });
        queryClient.invalidateQueries({ queryKey: portalConnectionQueryKeys.base });
        return true;
      } catch (error) {
        addToast({ text: error instanceof Error ? error.message : 'The portal scan failed.' });
        return false;
      } finally {
        busy.current = false;
        setActive(null);
      }
    },
    [addToast, portalSystems, queryClient]
  );

  // Sequential by construction: each sweep drives a browser tab, and the extension will refuse a
  // second one anyway. Stops at the first failure rather than marching the rest of the queue past
  // a problem the person should look at.
  const runScans = useCallback(
    async (workflowIds: string[]) => {
      for (const workflowId of workflowIds) {
        const ok = await runScan(workflowId);
        if (!ok) break;
      }
    },
    [runScan]
  );

  return { runScan, runScans, active, isRunning: active !== null };
}
